import React from 'react';
import { Link } from 'react-router-dom';
import { Archive, Award, Edit, Flag, Trash2, UserPlus } from 'lucide-react';
import { Button } from '../ui/button';
import { Can } from '../Can';
import { CourseThumb } from './CourseThumb';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { Course } from '../../types/learning.types';
import { courseContentTypeLabel, courseStatusPillClass, durationLabel } from '../../utils/learning';

interface CourseCardProps {
    course: Course;
    onAssign?: (course: Course) => void;
    onArchive?: (course: Course) => void;
    onDelete?: (course: Course) => void;
}

/**
 * Library card (prototype Course Library). Category colour is data, so it is applied as
 * an inline style; every status colour goes through the utils/learning helpers.
 */
export const CourseCard: React.FC<CourseCardProps> = ({ course, onAssign, onArchive, onDelete }) => {
    const category = course.category;
    const canDelete = course.status === 'Draft' && course.assignedCount === 0;
    const canArchive = course.status === 'Active';

    return (
        <div className="flex flex-col overflow-hidden rounded-xl border border-border bg-card">
            {/* ─── Thumbnail header ─── */}
            <div className="relative flex h-28 items-center justify-center">
                <CourseThumb
                    thumbnailUrl={course.thumbnailUrl}
                    categoryColour={category?.colour}
                    iconSize={34}
                />

                <span
                    className="absolute left-2.5 top-2.5 rounded-full border bg-white px-2 py-0.5 text-[11px] font-semibold"
                    style={{
                        color: category?.colour,
                        borderColor: category ? `${category.colour}33` : undefined,
                    }}
                >
                    {category?.name ?? 'Uncategorised'}
                </span>

                <span
                    className={`absolute right-2.5 top-2.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ${courseStatusPillClass(course.status)}`}
                >
                    {course.status}
                </span>

                <span className="absolute bottom-2.5 right-2.5 rounded-full bg-white/90 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                    {durationLabel(course.estimatedDurationMinutes)}
                </span>
            </div>

            {/* ─── Body ─── */}
            <div className="flex flex-1 flex-col gap-2 p-3.5">
                <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {courseContentTypeLabel(course.contentType)}
                    </span>
                    {course.showInOnboarding && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[11px] font-medium text-blue-600">
                            <Flag size={11} />
                            Onboarding
                        </span>
                    )}
                    {course.issuesCertificate && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700">
                            <Award size={11} />
                            Certificate
                        </span>
                    )}
                </div>

                <h3 className="text-[15px] font-semibold leading-snug">{course.title}</h3>

                <p className="line-clamp-2 text-[13px] leading-normal text-muted-foreground">
                    {course.description || '–'}
                </p>

                <div className="mt-auto pt-1.5 text-xs text-muted-foreground">
                    {course.assignedCount === 1
                        ? '1 learner assigned'
                        : `${course.assignedCount} learners assigned`}
                </div>

                {/* ─── Actions ─── */}
                <div className="flex items-center gap-2 border-t border-border pt-1.5">
                    <Can I={ACTIONS.UPDATE} a={RESOURCES.COURSE}>
                        <Button asChild variant="outline" className="h-8 flex-1">
                            <Link to={`/learning/courses/${course.id}/edit`}>
                                <Edit size={14} />
                                Edit
                            </Link>
                        </Button>
                    </Can>

                    {onAssign && (
                        <Can I={ACTIONS.CREATE} a={RESOURCES.COURSE_ASSIGNMENT}>
                            <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => onAssign(course)}
                                aria-label="Assign course"
                                title="Assign course"
                            >
                                <UserPlus size={15} />
                            </Button>
                        </Can>
                    )}

                    {canDelete && onDelete && (
                        <Can I={ACTIONS.DELETE} a={RESOURCES.COURSE}>
                            <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8 text-destructive"
                                onClick={() => onDelete(course)}
                                aria-label="Delete course"
                                title="Delete course"
                            >
                                <Trash2 size={15} />
                            </Button>
                        </Can>
                    )}

                    {canArchive && onArchive && (
                        <Can I={ACTIONS.UPDATE} a={RESOURCES.COURSE}>
                            <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground"
                                onClick={() => onArchive(course)}
                                aria-label="Archive course"
                                title="Archive course"
                            >
                                <Archive size={15} />
                            </Button>
                        </Can>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CourseCard;
